import { trpc } from './trpc'
import { inferRouterOutputs, inferRouterInputs } from '@trpc/server'
import { AppRouter } from '@tagadapay/trpc-types'
import { useToast } from '@/components/ui/use-toast'

type OutputRouter = inferRouterOutputs<AppRouter>
type InputRouter = inferRouterInputs<AppRouter>

// Output types
export type TranslationMessagesOutput = OutputRouter['translations']['getTranslationMessages']
export type StoreTranslationsOutput = OutputRouter['translations']['getStoreTranslations']
export type TranslationItem = StoreTranslationsOutput['translations'][number]
export type TranslationDetails = OutputRouter['translations']['getById']['translation']
export type TranslationsListOutput = OutputRouter['translations']['list']
export type TranslationListItem = TranslationsListOutput['items'][number]
export type CreateTranslationOutput = OutputRouter['translations']['create']
export type UpdateTranslationOutput = OutputRouter['translations']['update']
export type DeleteTranslationsOutput = OutputRouter['translations']['delete']
export type TranslationByStoreIdAndKeysOutput = OutputRouter['translations']['getByStoreIdAndKeys']
export type PushTranslationsOutput = OutputRouter['translations']['push']

// Input types
export type GetTranslationMessagesInput = InputRouter['translations']['getTranslationMessages']
export type GetStoreTranslationsInput = InputRouter['translations']['getStoreTranslations']
export type GetTranslationInput = InputRouter['translations']['getById']
export type ListTranslationsInput = InputRouter['translations']['list']
export type CreateTranslationInput = InputRouter['translations']['create']
export type UpdateTranslationInput = InputRouter['translations']['update']
export type DeleteTranslationsInput = InputRouter['translations']['delete']
export type GetTranslationByStoreIdAndKeysInput = InputRouter['translations']['getByStoreIdAndKeys']
export type PushTranslationsInput = InputRouter['translations']['push']

// Query hooks
export const useTranslationMessages = (params: GetTranslationMessagesInput, enabled = true) => {
	return trpc.translations.getTranslationMessages.useQuery(params, {
		enabled: enabled && !!params.storeId && !!params.locale,
	})
}

export const useStoreTranslations = (params: GetStoreTranslationsInput, enabled = true) => {
	return trpc.translations.getStoreTranslations.useQuery(params, {
		enabled: enabled && !!params.storeId,
	})
}

export const useTranslation = (id: string, enabled = true) => {
	return trpc.translations.getById.useQuery(
		{ id },
		{
			enabled: enabled && !!id,
			retry: 2,
			staleTime: 30000,
		},
	)
}

export const useTranslationsList = (params: ListTranslationsInput, options?: {
	enabled?: boolean;
	refetchOnMount?: boolean | 'always';
	refetchOnWindowFocus?: boolean;
}) => {
	const { enabled = true, ...otherOptions } = options || {};
	return trpc.translations.list.useQuery(params, {
		enabled,
		...otherOptions,
	})
}

export const useTranslationByStoreIdAndKeys = (params: GetTranslationByStoreIdAndKeysInput, enabled = true) => {
	return trpc.translations.getByStoreIdAndKeys.useQuery(params, {
		enabled: enabled && !!params.storeId,
	})
}

// Mutation hooks
export const useCreateTranslation = () => {
	const { toast } = useToast()
	const utils = trpc.useUtils()

	return trpc.translations.create.useMutation({
		onSuccess: () => {
			utils.translations.list.invalidate()
			utils.translations.getStoreTranslations.invalidate()
			toast({
				title: 'Success',
				description: 'Translation created successfully',
			})
		},
		onError: (error) => {
			toast({
				title: 'Error',
				description: error.message || 'Failed to create translation',
				variant: 'destructive',
			})
		},
	})
}

export const useUpdateTranslation = () => {
	const { toast } = useToast()
	const utils = trpc.useUtils()

	return trpc.translations.update.useMutation({
		onSuccess: () => {
			utils.translations.list.invalidate()
			utils.translations.getById.invalidate()
			utils.translations.getStoreTranslations.invalidate()
			toast({
				title: 'Success',
				description: 'Translation updated successfully',
			})
		},
		onError: (error) => {
			toast({
				title: 'Error',
				description: error.message || 'Failed to update translation',
				variant: 'destructive',
			})
		},
	})
}

export const useDeleteTranslations = () => {
	const { toast } = useToast()
	const utils = trpc.useUtils()

	return trpc.translations.delete.useMutation({
		onSuccess: () => {
			utils.translations.list.invalidate()
			utils.translations.getStoreTranslations.invalidate()
			toast({
				title: 'Success',
				description: 'Translation(s) deleted successfully',
			})
		},
		onError: (error) => {
			toast({
				title: 'Error',
				description: error.message || 'Failed to delete translation(s)',
				variant: 'destructive',
			})
		},
	})
}

export const usePushTranslations = () => {
	const { toast } = useToast()
	const utils = trpc.useUtils()

	return trpc.translations.push.useMutation({
		onSuccess: () => {
			utils.translations.list.invalidate()
			utils.translations.getStoreTranslations.invalidate()
			toast({
				title: 'Success',
				description: 'Translations pushed successfully',
			})
		},
		onError: (error) => {
			toast({
				title: 'Error',
				description: error.message || 'Failed to push translations',
				variant: 'destructive',
			})
		},
	})
}

